/**
 * 알림톡·문자 연동 탭 데이터 훅 — 발신번호 / 카카오 채널 / 발송 제한.
 *
 * be-notif Task#3 공표 계약(AdminNotificationIntegrationController). raw 엔드포인트라
 * adminFetch(401 자동복구) 사용. ApiResponse envelope, 목록은 `{items}` + meta. 쓰기는 Idempotency-Key.
 * ★기본설정(setDefault)은 전용 엔드포인트 없음 → PUT 편집(isDefault:true). 인증은 발신번호 생성 후
 *   per-id verify(send/confirm).
 */
import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor, releaseIdempotencyKey } from '@/lib/http-core';
import type {
  InspectionStatus,
  IntegrationStatus,
  KakaoChannel,
  KakaoChannelCreatePayload,
  KakaoChannelUpdatePayload,
  SendRestriction,
  SendRestrictionUpdatePayload,
  SenderNumber,
  SenderNumberCreatePayload,
  SenderNumberUpdatePayload,
  SenderPurpose,
  SenderVerificationSendResult,
  SenderVerifyConfirmPayload,
  VerificationStatus,
  LocaleMapping,
  LocaleMappingUpdatePayload,
  NotificationLocale,
  RecipientAllowlistCreatePayload,
  RecipientAllowlistEntry,
  RecipientAllowlistPage,
  RecipientAllowlistSettings,
  RecipientType,
  KakaoDispatchConfig,
} from '../types/notificationIntegration';

const ROOT = '/admin/v1/notification';
const SENDER_BASE = `${ROOT}/sender-numbers`;
const KAKAO_BASE = `${ROOT}/kakao-channels`;
const RESTRICTION_BASE = `${ROOT}/settings/send-restriction`;
const LOCALE_MAPPING_BASE = `${ROOT}/settings/locale-mapping`;
const ALLOWLIST_SETTINGS_BASE = `${ROOT}/settings/recipient-allowlist`;
const ALLOWLIST_BASE = `${ROOT}/recipient-allowlist`;

interface Envelope<T> {
  success: boolean;
  data?: T | null;
  error?: { code?: string; message?: string } | null;
}

export class IntegrationApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  constructor(status: number, code: string | undefined, message: string) {
    super(message);
    this.name = 'IntegrationApiError';
    this.status = status;
    this.code = code;
  }
}

const readEnvelope = async <T>(res: Response, fallback: string): Promise<T> => {
  let env: Envelope<T> | null = null;
  try {
    env = (await res.json()) as Envelope<T>;
  } catch {
    env = null;
  }
  if (!res.ok || !env || env.success !== true || env.data == null) {
    throw new IntegrationApiError(res.status, env?.error?.code, env?.error?.message ?? fallback);
  }
  return env.data;
};

const readNoContent = async (res: Response, fallback: string): Promise<void> => {
  if (res.status === 204 || res.ok) return;
  let env: Envelope<unknown> | null = null;
  try {
    env = (await res.json()) as Envelope<unknown>;
  } catch {
    env = null;
  }
  throw new IntegrationApiError(res.status, env?.error?.code, env?.error?.message ?? fallback);
};

// ── wire normalizers (be 계약 정합 + 방어) ──────────────────────
const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
const bool = (v: unknown): boolean => v === true;

const VERIFICATION_STATUSES: ReadonlyArray<VerificationStatus> = ['UNVERIFIED', 'PENDING', 'VERIFIED', 'FAILED'];
const asVerification = (v: unknown): VerificationStatus =>
  typeof v === 'string' && (VERIFICATION_STATUSES as ReadonlyArray<string>).includes(v)
    ? (v as VerificationStatus)
    : 'UNVERIFIED';

const INSPECTION_STATUSES: ReadonlyArray<InspectionStatus> = ['REQUESTED', 'APPROVED', 'REJECTED'];
const asInspection = (v: unknown): InspectionStatus =>
  typeof v === 'string' && (INSPECTION_STATUSES as ReadonlyArray<string>).includes(v)
    ? (v as InspectionStatus)
    : 'REQUESTED';

const asIntegrationStatus = (v: unknown): IntegrationStatus => (v === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE');

const SENDER_PURPOSES: ReadonlyArray<SenderPurpose> = ['SMS', 'ALIMTALK_FALLBACK', 'BOTH'];
const asPurpose = (v: unknown): SenderPurpose | null =>
  typeof v === 'string' && (SENDER_PURPOSES as ReadonlyArray<string>).includes(v) ? (v as SenderPurpose) : null;

type Rec = Record<string, unknown>;

const normalizeSender = (raw: Rec): SenderNumber => ({
  senderNumberId: String(raw.senderNumberId ?? raw.id ?? ''),
  maskedPhoneNumber: String(raw.maskedPhoneNumber ?? raw.phoneNumber ?? ''),
  description: str(raw.description),
  purpose: asPurpose(raw.purpose),
  purposeLabel: str(raw.purposeLabel),
  isDefault: bool(raw.isDefault),
  verificationStatus: asVerification(raw.verificationStatus),
  verificationStatusLabel: str(raw.verificationStatusLabel),
  status: asIntegrationStatus(raw.status),
  statusLabel: str(raw.statusLabel),
  createdAt: String(raw.createdAt ?? ''),
});

const normalizeKakao = (raw: Rec): KakaoChannel => ({
  kakaoChannelId: String(raw.kakaoChannelId ?? raw.id ?? ''),
  channelName: String(raw.channelName ?? ''),
  searchId: str(raw.searchId),
  category: str(raw.category),
  channelUrl: str(raw.channelUrl),
  maskedSenderKey: str(raw.maskedSenderKey),
  inspectionStatus: asInspection(raw.inspectionStatus),
  inspectionStatusLabel: str(raw.inspectionStatusLabel),
  status: asIntegrationStatus(raw.status),
  statusLabel: str(raw.statusLabel),
  isDefault: bool(raw.isDefault),
  createdAt: String(raw.createdAt ?? ''),
});

const normalizeRestriction = (raw: Rec): SendRestriction => ({
  nightBlockEnabled: bool(raw.nightBlockEnabled),
  nightStartTime: String(raw.nightStartTime ?? '21:00'),
  nightEndTime: String(raw.nightEndTime ?? '08:00'),
  duplicateBlockEnabled: bool(raw.duplicateBlockEnabled),
  duplicateWindowMinutes: typeof raw.duplicateWindowMinutes === 'number' ? raw.duplicateWindowMinutes : 60,
});

const normalizeSenderVerifySend = (raw: Rec): SenderVerificationSendResult => ({
  senderNumberId: String(raw.senderNumberId ?? ''),
  verificationStatus: asVerification(raw.verificationStatus),
  verificationStatusLabel: str(raw.verificationStatusLabel),
  expiresAt: str(raw.expiresAt),
});

/** 목록 응답 `{items}`(또는 content 폴백)을 배열로 평탄화. */
const asArray = (data: unknown): Rec[] => {
  if (Array.isArray(data)) return data as Rec[];
  if (data && typeof data === 'object') {
    const rec = data as Rec;
    if (Array.isArray(rec.items)) return rec.items as Rec[];
    if (Array.isArray(rec.content)) return rec.content as Rec[];
  }
  return [];
};

// ── 발신번호 ───────────────────────────────────────────────────
export const senderNumbersQueryKey = () => ['admin', 'notification', 'sender-numbers'] as const;

export const useSenderNumbersQuery = () =>
  useQuery<SenderNumber[]>({
    queryKey: senderNumbersQueryKey(),
    queryFn: async () => {
      const res = await adminFetch(`${SENDER_BASE}?size=100`);
      const data = await readEnvelope<unknown>(res, '발신번호 목록을 불러오지 못했습니다');
      return asArray(data).map(normalizeSender);
    },
    staleTime: 30_000,
  });

export const useCreateSenderNumberMutation = (): UseMutationResult<
  SenderNumber,
  IntegrationApiError,
  SenderNumberCreatePayload
> => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const res = await adminFetch(SENDER_BASE, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', SENDER_BASE, body) },
        body: JSON.stringify(body),
      });
      return normalizeSender(await readEnvelope<Rec>(res, '발신번호 등록에 실패했습니다'));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: senderNumbersQueryKey() }),
  });
};

export interface UpdateSenderInput {
  senderNumberId: string;
  body: SenderNumberUpdatePayload;
}

export const useUpdateSenderNumberMutation = (): UseMutationResult<
  SenderNumber,
  IntegrationApiError,
  UpdateSenderInput
> => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ senderNumberId, body }) => {
      const path = `${SENDER_BASE}/${senderNumberId}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', path, body) },
        body: JSON.stringify(body),
      });
      return normalizeSender(await readEnvelope<Rec>(res, '발신번호 수정에 실패했습니다'));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: senderNumbersQueryKey() }),
  });
};

export const useDeleteSenderNumberMutation = (): UseMutationResult<void, IntegrationApiError, string> => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const path = `${SENDER_BASE}/${id}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': idempotencyKeyFor('DELETE', path, null) },
      });
      await readNoContent(res, '발신번호 삭제에 실패했습니다');
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: senderNumbersQueryKey() }),
  });
};

/** 인증코드 발송 — 발신번호 생성 후 per-id. body 없음. */
export const useSenderVerifySendMutation = (): UseMutationResult<
  SenderVerificationSendResult,
  IntegrationApiError,
  string
> => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (senderNumberId) => {
      const path = `${SENDER_BASE}/${senderNumberId}/verify/send`;
      const res = await adminFetch(path, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', path, { senderNumberId }) },
      });
      return normalizeSenderVerifySend(await readEnvelope<Rec>(res, '인증코드 발송에 실패했습니다'));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: senderNumbersQueryKey() }),
  });
};

export interface ConfirmVerifyInput {
  senderNumberId: string;
  body: SenderVerifyConfirmPayload;
}

export const useSenderVerifyConfirmMutation = (): UseMutationResult<
  SenderNumber,
  IntegrationApiError,
  ConfirmVerifyInput
> => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ senderNumberId, body }) => {
      const path = `${SENDER_BASE}/${senderNumberId}/verify/confirm`;
      const res = await adminFetch(path, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', path, body) },
        body: JSON.stringify(body),
      });
      return normalizeSender(await readEnvelope<Rec>(res, '인증 확인에 실패했습니다'));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: senderNumbersQueryKey() }),
  });
};

// ── 카카오 채널 ─────────────────────────────────────────────────
export const kakaoChannelsQueryKey = () => ['admin', 'notification', 'kakao-channels'] as const;

/** 알림톡 현재 발송 설정(서버 설정값). BE 반영 전에는 404 — 화면은 "준비 중"으로 둔다(재시도 없음). */
export const useKakaoDispatchConfigQuery = () =>
  useQuery<KakaoDispatchConfig>({
    queryKey: ['notification', 'integration', 'kakao-dispatch-config'] as const,
    queryFn: async () => {
      const res = await adminFetch(`${KAKAO_BASE}/dispatch-config`);
      const raw = await readEnvelope<Rec>(res, '알림톡 발송 설정을 불러오지 못했습니다');
      return {
        provider: str(raw.provider),
        channelName: str(raw.channelName),
        searchId: str(raw.searchId),
        maskedSenderKey: str(raw.maskedSenderKey),
        liveDispatch: bool(raw.liveDispatch),
        maskedCallbackNumber: str(raw.maskedCallbackNumber),
        matchedKakaoChannelId: str(raw.matchedKakaoChannelId),
      };
    },
    retry: false,
    staleTime: 60_000,
  });

export const useKakaoChannelsQuery = () =>
  useQuery<KakaoChannel[]>({
    queryKey: kakaoChannelsQueryKey(),
    queryFn: async () => {
      const res = await adminFetch(`${KAKAO_BASE}?size=100`);
      const data = await readEnvelope<unknown>(res, '카카오 채널 목록을 불러오지 못했습니다');
      return asArray(data).map(normalizeKakao);
    },
    staleTime: 30_000,
  });

export const useCreateKakaoChannelMutation = (): UseMutationResult<
  KakaoChannel,
  IntegrationApiError,
  KakaoChannelCreatePayload
> => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const res = await adminFetch(KAKAO_BASE, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', KAKAO_BASE, body) },
        body: JSON.stringify(body),
      });
      return normalizeKakao(await readEnvelope<Rec>(res, '카카오 채널 등록에 실패했습니다'));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: kakaoChannelsQueryKey() }),
  });
};

export interface UpdateKakaoInput {
  kakaoChannelId: string;
  body: KakaoChannelUpdatePayload;
}

export const useUpdateKakaoChannelMutation = (): UseMutationResult<
  KakaoChannel,
  IntegrationApiError,
  UpdateKakaoInput
> => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ kakaoChannelId, body }) => {
      const path = `${KAKAO_BASE}/${kakaoChannelId}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', path, body) },
        body: JSON.stringify(body),
      });
      return normalizeKakao(await readEnvelope<Rec>(res, '카카오 채널 수정에 실패했습니다'));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: kakaoChannelsQueryKey() }),
  });
};

export const useDeleteKakaoChannelMutation = (): UseMutationResult<void, IntegrationApiError, string> => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const path = `${KAKAO_BASE}/${id}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': idempotencyKeyFor('DELETE', path, null) },
      });
      await readNoContent(res, '카카오 채널 삭제에 실패했습니다');
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: kakaoChannelsQueryKey() }),
  });
};

// ── 발송 제한(단일행) ───────────────────────────────────────────
export const sendRestrictionQueryKey = () => ['admin', 'notification', 'send-restriction'] as const;

export const useSendRestrictionQuery = () =>
  useQuery<SendRestriction>({
    queryKey: sendRestrictionQueryKey(),
    queryFn: async () => {
      const res = await adminFetch(RESTRICTION_BASE);
      return normalizeRestriction(await readEnvelope<Rec>(res, '발송 제한 설정을 불러오지 못했습니다'));
    },
    staleTime: 30_000,
  });

export const useUpdateSendRestrictionMutation = (): UseMutationResult<
  SendRestriction,
  IntegrationApiError,
  SendRestrictionUpdatePayload
> => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const res = await adminFetch(RESTRICTION_BASE, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', RESTRICTION_BASE, body) },
        body: JSON.stringify(body),
      });
      return normalizeRestriction(await readEnvelope<Rec>(res, '발송 제한 설정에 실패했습니다'));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: sendRestrictionQueryKey() }),
  });
};

// ── 국적별 발송 언어(로케일) 매핑 ───────────────────────────────
const LOCALES: ReadonlyArray<NotificationLocale> = ['KO_KR', 'EN_US', 'JA_JP', 'ZH_CN'];
const asLocale = (v: unknown, fallback: NotificationLocale): NotificationLocale =>
  typeof v === 'string' && (LOCALES as ReadonlyArray<string>).includes(v) ? (v as NotificationLocale) : fallback;

const normalizeLocaleMapping = (raw: Rec): LocaleMapping => {
  const defaultLocale = asLocale(raw.defaultLocale, 'EN_US');
  const mappings = Array.isArray(raw.mappings)
    ? (raw.mappings as Rec[])
        .map((m) => ({
          countryCode: String(m.countryCode ?? '').toUpperCase(),
          locale: asLocale(m.locale, defaultLocale),
        }))
        .filter((m) => m.countryCode.length > 0 && m.countryCode !== '*')
    : [];
  return { defaultLocale, mappings };
};

export const localeMappingQueryKey = () => ['admin', 'notification', 'locale-mapping'] as const;

export const useLocaleMappingQuery = () =>
  useQuery<LocaleMapping>({
    queryKey: localeMappingQueryKey(),
    queryFn: async () => {
      const res = await adminFetch(LOCALE_MAPPING_BASE);
      return normalizeLocaleMapping(await readEnvelope<Rec>(res, '발송 언어 매핑을 불러오지 못했습니다'));
    },
    staleTime: 30_000,
  });

export const useUpdateLocaleMappingMutation = (): UseMutationResult<
  LocaleMapping,
  IntegrationApiError,
  LocaleMappingUpdatePayload
> => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const res = await adminFetch(LOCALE_MAPPING_BASE, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', LOCALE_MAPPING_BASE, body) },
        body: JSON.stringify(body),
      });
      return normalizeLocaleMapping(await readEnvelope<Rec>(res, '발송 언어 매핑 저장에 실패했습니다'));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: localeMappingQueryKey() }),
  });
};

// ── 수신자 허용 목록(테스트 모드, notification V37) ─────────────────
// QA·오픈 전 운영 DB 에 이관된 실제 고객 연락처가 있어, 테스트 중 알림이 실제 고객에게 나가지 않게 한다.
// 모드를 켜면 목록의 번호·메일·테스트 회원 계정으로만 발송되고 나머지는 ALLOWLIST_BLOCKED 로 막힌다.
// 조회 SUPER_ADMIN·OPS_ADMIN·CS_AGENT·AUDITOR / 변경 SUPER_ADMIN·OPS_ADMIN. 쓰기는 Idempotency-Key 필수.
const num0 = (v: unknown): number => (typeof v === 'number' ? v : 0);
const RECIPIENT_TYPES: ReadonlyArray<RecipientType> = ['PHONE', 'EMAIL', 'ACCOUNT'];
const asRecipientType = (v: unknown): RecipientType =>
  typeof v === 'string' && (RECIPIENT_TYPES as ReadonlyArray<string>).includes(v) ? (v as RecipientType) : 'PHONE';

const normalizeAllowlistSettings = (raw: Rec): RecipientAllowlistSettings => ({
  enabled: bool(raw.enabled),
  phoneCount: num0(raw.phoneCount),
  emailCount: num0(raw.emailCount),
  accountCount: num0(raw.accountCount),
  updatedAt: str(raw.updatedAt),
});

const normalizeAllowlistEntry = (raw: Rec): RecipientAllowlistEntry => ({
  allowlistId: String(raw.allowlistId ?? raw.id ?? ''),
  recipientType: asRecipientType(raw.recipientType),
  recipientValue: String(raw.recipientValue ?? ''),
  label: str(raw.label),
  createdAt: String(raw.createdAt ?? ''),
});

export const recipientAllowlistSettingsQueryKey = () =>
  ['admin', 'notification', 'recipient-allowlist', 'settings'] as const;
export const recipientAllowlistListQueryKey = (recipientType: RecipientType | null, page: number, size: number) =>
  ['admin', 'notification', 'recipient-allowlist', 'list', recipientType, page, size] as const;
const invalidateAllowlist = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: ['admin', 'notification', 'recipient-allowlist'] });

export const useRecipientAllowlistSettingsQuery = () =>
  useQuery<RecipientAllowlistSettings, IntegrationApiError>({
    queryKey: recipientAllowlistSettingsQueryKey(),
    queryFn: async () => {
      const res = await adminFetch(ALLOWLIST_SETTINGS_BASE);
      return normalizeAllowlistSettings(await readEnvelope<Rec>(res, '수신자 허용 목록 설정을 불러오지 못했습니다'));
    },
    staleTime: 30_000,
  });

export const useUpdateRecipientAllowlistSettingsMutation = (): UseMutationResult<
  RecipientAllowlistSettings,
  IntegrationApiError,
  { enabled: boolean }
> => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const res = await adminFetch(ALLOWLIST_SETTINGS_BASE, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', ALLOWLIST_SETTINGS_BASE, body) },
        body: JSON.stringify(body),
      });
      const view = normalizeAllowlistSettings(await readEnvelope<Rec>(res, '테스트 모드 변경에 실패했습니다'));
      // 켜기→끄기→켜기 처럼 같은 본문을 다시 보낼 때 첫 응답 재생으로 묻히지 않게 키를 푼다.
      releaseIdempotencyKey('PUT', ALLOWLIST_SETTINGS_BASE, body);
      return view;
    },
    onSuccess: () => void invalidateAllowlist(qc),
  });
};

/** 목록 — recipientType 생략 시 전체. 페이지 정보는 envelope meta(total/page/size)에 있다. page 는 0부터. */
export const useRecipientAllowlistQuery = (recipientType: RecipientType | null, page: number, size = 20) =>
  useQuery<RecipientAllowlistPage>({
    queryKey: recipientAllowlistListQueryKey(recipientType, page, size),
    queryFn: async () => {
      const qs = new URLSearchParams({ page: String(page), size: String(size) });
      if (recipientType) qs.set('recipientType', recipientType);
      const res = await adminFetch(`${ALLOWLIST_BASE}?${qs.toString()}`);
      let env: (Envelope<unknown> & { meta?: { total?: number } | null }) | null = null;
      try {
        env = (await res.json()) as Envelope<unknown> & { meta?: { total?: number } | null };
      } catch {
        env = null;
      }
      if (!res.ok || !env || env.success !== true) {
        throw new IntegrationApiError(res.status, env?.error?.code, env?.error?.message ?? '수신자 허용 목록을 불러오지 못했습니다');
      }
      const items = asArray(env.data).map(normalizeAllowlistEntry);
      return { items, total: typeof env.meta?.total === 'number' ? env.meta.total : items.length };
    },
    staleTime: 30_000,
  });

export const useCreateRecipientAllowlistMutation = (): UseMutationResult<
  RecipientAllowlistEntry,
  IntegrationApiError,
  RecipientAllowlistCreatePayload
> => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const res = await adminFetch(ALLOWLIST_BASE, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', ALLOWLIST_BASE, body) },
        body: JSON.stringify(body),
      });
      const view = normalizeAllowlistEntry(await readEnvelope<Rec>(res, '수신자 등록에 실패했습니다'));
      // 등록→삭제→같은 값 재등록이 첫 응답 재생으로 묻히지 않게.
      releaseIdempotencyKey('POST', ALLOWLIST_BASE, body);
      return view;
    },
    onSuccess: () => void invalidateAllowlist(qc),
  });
};

export const useDeleteRecipientAllowlistMutation = (): UseMutationResult<void, IntegrationApiError, string> => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (allowlistId) => {
      const path = `${ALLOWLIST_BASE}/${encodeURIComponent(allowlistId)}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': idempotencyKeyFor('DELETE', path, null) },
      });
      await readNoContent(res, '수신자 삭제에 실패했습니다');
    },
    onSuccess: () => void invalidateAllowlist(qc),
  });
};
